import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { BugReport, BugReportStatus, BugReportPriority } from '@/types/bugReport';
import { useToast } from '@/hooks/use-toast';

interface SubmitBugReportData {
  title: string;
  description: string;
  steps_to_reproduce?: string;
  page_url?: string;
  browser_info?: string;
  attachments?: string[];
}

export function useBugReports() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all bug reports (admin only - RLS handles this)
  const { data: bugReports = [], isLoading } = useQuery({
    queryKey: ['bugReports'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bug_reports')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as BugReport[];
    },
    enabled: !!user?.id,
  });

  // Submit a new bug report
  const submitReport = useMutation({
    mutationFn: async (data: SubmitBugReportData) => {
      if (!user?.id) throw new Error('Must be logged in');
      
      const { error } = await supabase
        .from('bug_reports')
        .insert({
          user_id: user.id,
          title: data.title,
          description: data.description,
          steps_to_reproduce: data.steps_to_reproduce || null,
          page_url: data.page_url || null,
          browser_info: data.browser_info || null,
          attachments: data.attachments || [],
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: 'Bug report submitted',
        description: 'Thank you for your feedback! We\'ll look into it.',
      });
      queryClient.invalidateQueries({ queryKey: ['bugReports'] });
    },
    onError: (error) => {
      console.error('Error submitting bug report:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit bug report. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Update bug report status (admin only)
  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: BugReportStatus }) => {
      const updates: Partial<BugReport> = { status };
      if (status === 'fixed' || status === 'wont_fix') {
        updates.resolved_at = new Date().toISOString();
      } else {
        updates.resolved_at = null;
      }
      
      const { error } = await supabase
        .from('bug_reports')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bugReports'] });
      toast({ title: 'Status updated' });
    },
  });

  // Update bug report priority (admin only)
  const updatePriority = useMutation({
    mutationFn: async ({ id, priority }: { id: string; priority: BugReportPriority }) => {
      const { error } = await supabase
        .from('bug_reports')
        .update({ priority })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bugReports'] });
      toast({ title: 'Priority updated' });
    },
  });

  // Update admin notes (admin only)
  const updateNotes = useMutation({
    mutationFn: async ({ id, admin_notes }: { id: string; admin_notes: string }) => {
      const { error } = await supabase
        .from('bug_reports')
        .update({ admin_notes })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bugReports'] });
      toast({ title: 'Notes saved' });
    },
  });

  // Delete bug report (admin only)
  const deleteReport = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('bug_reports')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bugReports'] });
      toast({ title: 'Bug report deleted' });
    },
  });

  const newCount = bugReports.filter(r => r.status === 'new').length;

  return {
    bugReports,
    isLoading,
    newCount,
    submitReport,
    updateStatus,
    updatePriority,
    updateNotes,
    deleteReport,
  };
}
